/**
 * Multi-timeframe calc wrapper — runs a blessed indicator's math over
 * higher-timeframe buckets and projects the values back onto the chart's bars.
 *
 * This is the `timeframe` param the HTF overlay's docblock deferred to: rather
 * than an indicator that references other indicators (a dependency graph), the
 * indicator's OWN calc runs over aggregated bars. The wrapper composes around
 * the app-owned clone calcs (maInstances / maBandIndicators), which exist
 * because klinecharts' built-in templates are not exported.
 *
 * Intraday targets (5m to 4H) bucket through `projectHtfBuckets`, so an HTF
 * candle overlay and a 1H EMA can never disagree about where a bucket starts.
 * The last bucket is `forming`: computed from its partial bars, so the
 * projected value is the DEVELOPING value and repaints as bars arrive, the
 * display semantics Sierra and other pro charting tools both use.
 *
 * Session targets (1D, 1W) cannot clock-floor: a UTC-floored day starts at
 * 19:00/20:00 New York time, not at the open, and an intraday chart never
 * holds the year of history a 200-day line needs. They read a fetched daily
 * series through `extendData.readHtf` and map it by the session rules in
 * mtfSession (Phase B). Today's row still develops from the chart's own bars.
 *
 * Values step: every chart bar in a bucket shares the SAME result object
 * (reference-shared, the HtfPoint precedent), holding flat across the bucket.
 */

import type { KLineData } from 'klinecharts'
import { projectHtfBuckets, type HtfBucket } from './htfProjection'
import {
  isSessionTimeframe,
  projectChartWeeks,
  projectSessionTimeframe,
  type SessionRow,
} from './mtfSession'

/** Timeframes the settings UI offers, coarse enough to matter. 1D and 1W are
 *  session timeframes (see mtfSession); the rest are clock buckets. */
export const MTF_TIMEFRAMES = ['5m', '10m', '15m', '30m', '1H', '2H', '4H', '1D', '1W'] as const
export type MtfTimeframe = (typeof MTF_TIMEFRAMES)[number]

const MINUTE = 60_000
const TIMEFRAME_MS: Record<MtfTimeframe, number> = {
  '5m': 5 * MINUTE,
  '10m': 10 * MINUTE,
  '15m': 15 * MINUTE,
  '30m': 30 * MINUTE,
  '1H': 60 * MINUTE,
  '2H': 120 * MINUTE,
  '4H': 240 * MINUTE,
  '1D': 24 * 60 * MINUTE,
  '1W': 7 * 24 * 60 * MINUTE,
}
const DAY_MS = TIMEFRAME_MS['1D']

/** Milliseconds for a timeframe token, or undefined for anything unknown. */
export function mtfTimeframeMs(timeframe: unknown): number | undefined {
  return typeof timeframe === 'string' ? TIMEFRAME_MS[timeframe as MtfTimeframe] : undefined
}

/** The extendData fields the wrapper reads (the TPO `periodMs` precedent). */
export interface MtfExtendData {
  timeframe?: string
  periodMs?: number
  /** 1D/1W only: the fetched daily rows for the chart's ticker, or undefined
   *  while they load. Attached by ChartManager, never persisted. */
  readHtf?: () => readonly SessionRow[] | undefined
  /** 1D/1W only: the chart's present, replay-aware (ChartManager). */
  nowMs?: () => number
}

/** Calc shape the wrapper accepts and returns — the clone-calc contract. */
export type MtfWrappableCalc = (
  dataList: KLineData[],
  indicator: { calcParams?: number[]; extendData?: unknown },
) => Record<string, number>[]

/**
 * Chart bar interval inferred from the data itself: the smallest positive gap
 * over the leading deltas. Session gaps and holes only ever make a delta
 * LARGER, so the minimum is the true bar interval.
 *
 * Inference (rather than trusting a stamped `periodMs` alone) is what makes a
 * period switch safe: an extendData written on a 1H chart would otherwise
 * still say 1H after the user drops to 5m, and a stale period is the
 * difference between aggregating and silently passing through.
 */
function inferBarIntervalMs(dataList: KLineData[]): number {
  let min = Infinity
  const limit = Math.min(dataList.length, 50)
  for (let i = 1; i < limit; i++) {
    const delta = dataList[i].timestamp - dataList[i - 1].timestamp
    if (delta > 0 && delta < min) min = delta
  }
  return Number.isFinite(min) ? min : 0
}

/** Shared row for bars no bucket value applies to (warm-up, unmapped). One
 *  frozen object rather than N fresh ones — klinecharts only reads rows. */
const EMPTY_ROW: Record<string, number> = Object.freeze({})

/**
 * Wrap a calc so it honours `extendData.timeframe`.
 *
 * Passthrough — byte-identical behavior to the base calc — whenever the
 * timeframe is unset, unknown, or not strictly higher than the chart's own
 * period. A 15m "higher" timeframe on a 1H chart is not a thing, and failing
 * open to the chart timeframe beats rendering a line that silently duplicates
 * the price series. The chart period comes from `extendData.periodMs` when
 * supplied, else it is inferred from the bars (see inferBarIntervalMs).
 */
export function withTimeframe(baseCalc: MtfWrappableCalc): MtfWrappableCalc {
  return (dataList, indicator) => {
    const ext = indicator.extendData as MtfExtendData | undefined
    const targetMs = mtfTimeframeMs(ext?.timeframe)
    const explicit = Number(ext?.periodMs)
    const chartMs =
      Number.isFinite(explicit) && explicit > 0 ? explicit : inferBarIntervalMs(dataList)
    if (!targetMs || !(chartMs > 0) || targetMs <= chartMs) {
      return baseCalc(dataList, indicator)
    }

    if (isSessionTimeframe(ext?.timeframe)) {
      // A daily chart already holds the sessions a weekly value needs.
      if (chartMs >= DAY_MS) return projectChartWeeks(baseCalc, dataList, indicator)
      // Still loading (or no reader): no line. Falling back to the chart's
      // own values would draw an intraday EMA labelled as a daily one.
      const fetched = typeof ext?.readHtf === 'function' ? ext.readHtf() : undefined
      if (!fetched) return dataList.map(() => EMPTY_ROW)
      return projectSessionTimeframe(baseCalc, dataList, indicator, ext.timeframe, {
        fetched,
        chartMs,
        nowMs: typeof ext.nowMs === 'function' ? ext.nowMs() : Date.now(),
      })
    }

    const projected = projectHtfBuckets(dataList, targetMs, chartMs)

    // Distinct buckets in chart order (projectHtfBuckets shares one object per
    // bucket, so a reference change IS a boundary), plus each bar's bucket
    // ordinal for the projection back.
    const bucketBars: KLineData[] = []
    const bucketOrdinal: number[] = new Array(dataList.length).fill(-1)
    let prev: HtfBucket | undefined
    for (let i = 0; i < projected.length; i++) {
      const bucket = projected[i]
      if (!bucket) continue
      if (bucket !== prev) {
        // Volume is deliberately absent: the blessed set (MA family, BOLL,
        // ATR) is close/high/low math. A volume-weighted candidate would need
        // HtfBucket to carry summed volume first.
        bucketBars.push({
          timestamp: bucket.bucketStart,
          open: bucket.open,
          high: bucket.high,
          low: bucket.low,
          close: bucket.close,
          volume: 0,
        } as KLineData)
        prev = bucket
      }
      bucketOrdinal[i] = bucketBars.length - 1
    }

    // The indicator's own math, unchanged, over the coarser bars — same
    // calcParams, so EMA(20) means 20 HTF buckets, exactly as on an HTF chart.
    const htfRows = baseCalc(bucketBars, indicator)

    const out: Record<string, number>[] = new Array(dataList.length)
    for (let i = 0; i < dataList.length; i++) {
      const ordinal = bucketOrdinal[i]
      out[i] = ordinal >= 0 ? (htfRows[ordinal] ?? EMPTY_ROW) : EMPTY_ROW
    }
    return out
  }
}
